import React from "react";
import { FormattedMessage } from "react-intl";
import { UiTrackedButton } from "../common/TrackedComponents";
import type { DeliveryDates } from "../../stores/DatesProvider";
import { DeliveryTimeSlot } from "../../server/StoreInfoRequests";
import { formatTimeSlots } from "../common/DeliveryTimeSlots";
import { formatLocalDate, isSameOrBefore, parseDate } from "../../utils/DateUtil";
import { compareAsc, isAfter } from "date-fns";

type DeliveryDateSuggestionsProps = {
	dates: DeliveryDates;
	selectedDate: Date | null | undefined;
	onSelectDate: (date: Date) => void;
	earliestItemDates: Set<string>;
	localDateFormat: string;
};
type SuggestionData = {
	date: string;
	deliveryCount: number | null | undefined;
	deliveryTimes: DeliveryTimeSlot[] | null | undefined;
};
export const DeliveryDateSuggestions = ({
	dates,
	selectedDate,
	onSelectDate,
	earliestItemDates,
	localDateFormat,
}: DeliveryDateSuggestionsProps) => {
	const suggestions: SuggestionData[] = assembleSuggestions(dates, earliestItemDates);
	if (!suggestions.length) return <FormattedMessage id="ORC_NO_SUGGESTABLE_DATES_TEXT" />;
	return (
		<div className="mfcss_datepicker_suggestions">
			{suggestions.map((dateAndDeliveryCount) => {
				const dateString = dateAndDeliveryCount.date;
				const date = parseDate(dateString);
				const isSelected = !!selectedDate && date.getTime() === selectedDate.getTime();
				const deliverySlotText = getDeliverySlotText(dateAndDeliveryCount);
				return (
					<UiTrackedButton
						key={dateString}
						tags={{
							source: "DateInputPicker.validDate",
						}}
						data-grow
						data-variant={isSelected ? "action" : ""}
						className={isSelected ? "selected" : ""}
						onClick={() => onSelectDate(date)}
					>
						<span>{formatLocalDate(date, localDateFormat)}</span>
						<span className="mfcss_datepicker_timeslot margin-grid-left">{deliverySlotText}</span>
					</UiTrackedButton>
				);
			})}
		</div>
	);

	function getDeliverySlotText(dateAndDeliveryCount: SuggestionData) {
		return dateAndDeliveryCount.deliveryTimes?.[0]
			? formatTimeSlots(parseDate(dateAndDeliveryCount.date), dateAndDeliveryCount.deliveryTimes)
			: "";
	}
};

const assembleSuggestions = (dates: DeliveryDates, earliestItemDates: Set<string>): SuggestionData[] => {
	const now = new Date();
	if (earliestItemDates.size === 0) {
		const shouldSuggest = (date: string) => {
			const dateInfo = dates.get(date);
			return (
				dateInfo &&
				dateInfo.type === "ValidDate" &&
				dateInfo.isCustomerSlot &&
				!isAfter(now, parseDate(dateInfo.leadTimeCompliantUntil))
			);
		};

		return [...dates.keys()]
			.sort(compareAsc)
			.filter(shouldSuggest)
			.slice(0, 3)
			.map((date) => {
				return {
					date,
					deliveryCount: null,
					deliveryTimes: (dates.get(date) as any).deliveryTimes,
				};
			});
	}

	const deliverableItemCountUntil = (date: string): number | null | undefined => {
		const dateInfo = dates.get(date);

		if (
			!dateInfo ||
			dateInfo.type !== "ValidDate" ||
			!dateInfo.isCustomerSlot ||
			isAfter(now, parseDate(dateInfo.leadTimeCompliantUntil))
		) {
			return null;
		}

		return [...earliestItemDates].filter(
			(itemDate) => !itemDate || isSameOrBefore(parseDate(itemDate), parseDate(date)),
		).length;
	};

	const datesAndDeliverableItemCounts = [...dates.keys()]
		.sort(compareAsc)
		.map((date) => [date, deliverableItemCountUntil(date)])
		.filter(([, itemCount]) => !!itemCount);
	if (!datesAndDeliverableItemCounts.length) return [];
	const datesAndDeliveryCounts = datesAndDeliverableItemCounts.map(([date], index) => {
		// Deduce total number of deliveries from number of distinct non-zero item counts at or after this slot.
		// This works, because don't need to worry about items not deliverable beyond the date picker's scope.
		const deliveryCount = new Set(
			datesAndDeliverableItemCounts
				.slice(index)
				.map(([, items]) => items)
				.filter((itemCount) => !!itemCount),
		).size;
		return {
			date,
			deliveryCount,
			deliveryTimes: (dates.get(date as string) as any).deliveryTimes,
		};
	});
	const suggestions = datesAndDeliveryCounts.slice(0, 2);
	const thirdSuggestion =
		datesAndDeliveryCounts.slice(2).find((dateAndDeliveryCount) => dateAndDeliveryCount.deliveryCount === 1) ||
		datesAndDeliveryCounts[2];

	if (thirdSuggestion) {
		suggestions.push(thirdSuggestion);
	}

	return suggestions as SuggestionData[];
};
